import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BarChart3, PieChart, TrendingUp, BarChart2 } from 'lucide-react';
import Chart from 'chart.js/auto';
import toast from 'react-hot-toast';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';
import { downloadJson } from '../../utils/exportHelpers';

const hasChartValues = (chartData) => {
  if (!chartData?.labels?.length || !chartData?.datasets?.length) return false;
  return chartData.datasets.some((ds) => (ds.data || []).some((v) => Number(v) > 0));
};

const ChartCard = ({ title, data, type, icon: Icon, periodLabel }) => {
  const getChartIcon = () => {
    switch (type) {
      case 'pie':
        return <PieChart className="w-5 h-5" />;
      case 'line':
        return <TrendingUp className="w-5 h-5" />;
      default:
        return <BarChart3 className="w-5 h-5" />;
    }
  };

  const [filterOpen, setFilterOpen] = useState(false);
  const [hideZeroPie, setHideZeroPie] = useState(false);
  const [lineVisible, setLineVisible] = useState([]);

  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (data?.datasets?.length && type === 'line') {
      setLineVisible((prev) => {
        if (prev.length === data.datasets.length) return prev;
        return Array(data.datasets.length).fill(true);
      });
    }
  }, [data, type]);

  const displayData = useMemo(() => {
    if (!data || !data.labels || !data.datasets?.length) return data;

    if (type === 'pie' && hideZeroPie) {
      const ds = data.datasets[0];
      const raw = ds.data || [];
      const idx = [];
      for (let i = 0; i < data.labels.length; i++) {
        if ((raw[i] || 0) > 0) idx.push(i);
      }
      if (idx.length === data.labels.length) return data;
      const newLabels = idx.map((i) => data.labels[i]);
      const newData = idx.map((i) => raw[i]);
      const bg = ds.backgroundColor;
      const newBg = Array.isArray(bg) ? idx.map((i) => bg[i]) : bg;
      return {
        labels: newLabels,
        datasets: [{ ...ds, data: newData, backgroundColor: newBg }],
      };
    }

    if (type === 'line' && lineVisible.length === data.datasets.length) {
      const anyHidden = lineVisible.some((v) => !v);
      if (!anyHidden) return data;
      const datasets = data.datasets.filter((_, i) => lineVisible[i]);
      if (datasets.length === 0) return data;
      return { ...data, datasets };
    }

    return data;
  }, [data, type, hideZeroPie, lineVisible]);

  const chartReady = hasChartValues(displayData);

  useEffect(() => {
    if (!canvasRef.current || !chartReady) return;

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const ctx = canvasRef.current.getContext('2d');
    const chartData = displayData;

    const options =
      type === 'pie'
        ? {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: 8 },
            plugins: {
              legend: { position: 'bottom', labels: { boxWidth: 10, padding: 12 } },
              tooltip: { enabled: true },
            },
            cutout: '55%',
          }
        : {
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { grid: { display: false } },
              y: { grid: { color: 'rgba(0,0,0,0.05)' }, beginAtZero: true },
            },
            plugins: {
              legend: { position: 'bottom' },
              tooltip: { enabled: true },
            },
          };

    const chartType = type === 'pie' ? 'doughnut' : type === 'bar' ? 'bar' : 'line';
    chartRef.current = new Chart(ctx, {
      type: chartType,
      data: chartData,
      options,
    });

    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [displayData, type, chartReady]);

  const safeFilename = (s) =>
    String(s || 'grafico')
      .replace(/[^\w\-]+/g, '-')
      .replace(/^-|-$/g, '') || 'grafico';

  const handleExportPng = () => {
    try {
      if (!chartReady) {
        toast.error('Não há dados para exportar.');
        return;
      }
      const canvas = canvasRef.current;
      if (!canvas) {
        toast.error('Gráfico ainda não está pronto.');
        return;
      }
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeFilename(title)}.png`;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Imagem exportada.');
    } catch (e) {
      toast.error('Não foi possível exportar a imagem.');
    }
  };

  const handleExportJson = () => {
    if (!chartReady) {
      toast.error('Não há dados para exportar.');
      return;
    }
    const payload = displayData && displayData.labels ? displayData : data;
    downloadJson(`${safeFilename(title)}.json`, { title, type, data: payload });
    toast.success('JSON exportado.');
  };

  const toggleLineDataset = (index) => {
    setLineVisible((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
            {getChartIcon()}
          </div>
          <div>
            <h3 className="card-title">{title}</h3>
            <p className="card-subtitle">{periodLabel || 'Últimos 30 dias'}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Can permission={P.DASHBOARD_VIEW}>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => setFilterOpen((v) => !v)}
              aria-expanded={filterOpen}
            >
              Filtrar
            </button>
          </Can>
          <Can permission={P.DASHBOARD_VIEW}>
            <div className="relative inline-block text-left">
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleExportPng}
                title="Exportar PNG"
              >
                Exportar PNG
              </button>
            </div>
          </Can>
          <Can permission={P.DASHBOARD_VIEW}>
            <button type="button" className="btn btn-outline btn-sm" onClick={handleExportJson} title="Exportar dados JSON">
              JSON
            </button>
          </Can>
        </div>
      </div>
      {filterOpen && (
        <div className="border-b border-gray-100 bg-slate-50/80 px-5 py-3 text-sm sm:px-6">
          {type === 'pie' && (
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={hideZeroPie}
                onChange={(e) => setHideZeroPie(e.target.checked)}
                className="rounded border-gray-300"
              />
              Ocultar status sem licenças (valor zero)
            </label>
          )}
          {type === 'line' && data?.datasets?.length > 0 && (
            <div className="space-y-2">
              <span className="font-medium text-gray-700">Séries exibidas:</span>
              {data.datasets.map((ds, i) => (
                <label key={ds.label || i} className="flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={lineVisible[i] !== false}
                    onChange={() => toggleLineDataset(i)}
                    className="rounded border-gray-300"
                  />
                  {ds.label || `Série ${i + 1}`}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="card-content">
        {chartReady ? (
          <div className="h-64 bg-white">
            <canvas ref={canvasRef} className="h-full w-full" />
          </div>
        ) : (
          <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg bg-gray-50 text-center">
            <BarChart2 className="h-10 w-10 text-gray-300" />
            <p className="text-sm font-medium text-gray-700">Sem dados para exibir</p>
            <p className="max-w-xs text-sm text-gray-500">
              Cadastre licenças para visualizar este gráfico.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartCard;
